import { generateId } from "@primaria/plugins-core";
import { html } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { translate } from "../../../../localization";
import {
  ActivityHistoryFilterType,
  IActivityHistoryCommonFilter,
  IActivityHistoryCustomFilter,
  IActivityHistoryCustomFilterGroup,
  IActivityHistoryFilter,
} from "../../model";
import { ActivityHistoryFilters } from "./activity-history-filters";

const filterTemplates = {
  [ActivityHistoryFilterType.Switch]: (id, title, handleChange, isSelected, isEnabled) => html`
    <dss-input-switch size="sm" @onChangeValue=${handleChange} .checked=${isSelected}>
      <input slot="input" id="switch-${id}" aria-label=${title} type="checkbox"  ?disabled=${!isEnabled}>
      <label slot="label" for="switch-${id}">${title || id}</label>
    </dss-input-switch>`,

  [ActivityHistoryFilterType.Checkbox]: (id, title, handleChange, isSelected, isEnabled) => html`
    <dss-checkbox variant="default" @onChange=${(e) => handleChange(e)}> 
      <input slot="input" id="checkboxStory-${id}" type="checkbox" .checked=${isSelected} ?disabled=${!isEnabled}>
      <label slot="label" for="checkboxStory-${id}">${title || id}</label>
    </dss-checkbox>`,

  /* [ActivityHistoryFilterType.Dropdown]: (id, title, handleChange, isSelected, isEnabled) => html`
    <dss-input-dropdown type="default" @onChange=${(e) => handleChange(e)}> 
      <input slot="input" id="dropdownStory-${id}" type="text" .checked=${isSelected} ?disabled=${!isEnabled}>
      <label slot="label" for="dropdownStory-${id}">${title || id}</label>
    </dss-input-dropdown>`, */
};

const getFilterTemplate = (
  filter: IActivityHistoryCommonFilter & IActivityHistoryCustomFilter,
  onChangeValue: (...args: any[]) => void,
  filterGroup?: IActivityHistoryFilter,
  option?: { id: string; title: string },
) => {
  const isCustom = !!filterGroup?.id && !!option;
  //const id = isCustom ? `${filterGroupId}-${filter.id}-${option.id}` : filter.id;
  const id = generateId();
  const title = isCustom ? option.title || option.id : filter.title;

  const handleChange = (e) => {
    if (filter.singleOption) onChangeValue(filterGroup?.id, filter.id, null, e.detail);
    else if (isCustom && option) onChangeValue(filterGroup?.id, filter.id, option.id, e.detail);
    else onChangeValue(filter.id, e.detail);
  };

  const isSelected =
    isCustom && option ? (filter as IActivityHistoryCustomFilter).enabledValues?.includes(option.id) : filter.enabled;

  const isEnabled =
    (isCustom && option) || filter.singleOption ? (filterGroup as IActivityHistoryCustomFilterGroup).enabled : true;

  return filterTemplates[filter.type]?.(id, title, handleChange, isSelected, isEnabled) || html``;
};

export const template = (props: ActivityHistoryFilters) =>
  html`
  <div class="filters-sidebar" ?expanded=${props.filtersExpanded} >
      ${
        props.filtersExpanded
          ? html`<div class="filters-sidebar-opened">${translate("filters.filters")}<dss-icon-button ?hidden=${props.wrapperMaximized} @click=${() => props._toggleFilters()} size="md" variant="primary" icon="keyboard_double_arrow_left"></dss-icon-button></div>
          <div class="filter-container">
                  ${repeat(props.commonFilters, (filter: IActivityHistoryCommonFilter) => html`<div class="filter">${getFilterTemplate(filter, props._onChangeCommonFilterValue.bind(props))}</div> `)}
                  <!-- Regió per al filtre de diagnostics extern -->
                  <div class="filter">
                    <primaria-region name=${props.api.regionManager.regions.activityHistory.customFilters}></primaria-region>
                  </div>
                  ${
                    props.upFilter &&
                    html`<div class="filter">
                      <dss-input-dropdown inputsize="md" icon="" multiple dropdownFixed type="default" .elements=${props.upFilter?.values} @onInputDropdownChange=${props._handleUpFilterChange} .selectedValue=${props.upFilterEnabledValues} selectorStyle=${"max-height: 450px"}>
                        <label slot="label" for="up-filter">${props.upFilter.title}</label>
                        <input id="up-filter" slot="input" type="text" class="dss-input" autocomplete="off"/>
                      </dss-input-dropdown>
                    </div>`
                  }
                  ${repeat(
                    props.customFilterGroups,
                    (group: IActivityHistoryCustomFilterGroup) => html`
                  <div class="filter-group">
                    <dss-input-switch size="sm" .checked=${group.enabled} @onChangeValue=${(e) => props._onChangeCustomFilterGroupValue(group.id, e.detail)}>
                      <input slot="input" id="switch-${group.id}" aria-label=${group.title} type="checkbox">
                      <label slot="label" for="switch-${group.id}">${group.title}</label>
                    </dss-input-switch>
                    <div class="custom-subfilters-container">
                      ${
                        group?.filters &&
                        repeat(group.filters, (filter: IActivityHistoryCustomFilter) =>
                          customFilterTemplate(filter, props, group),
                        )
                      }
                    </div>
                  </div> `,
                  )}
                </div> `
          : html`
            <dss-icon-button tooltipFixed label=${translate("filters.filters")} @click=${() => props._toggleFilters()} size="md" variant="primary" icon="filter_list"></dss-icon-button>
          `
      }
  </div>
  `;

const customFilterTemplate = (
  filter: IActivityHistoryCustomFilter,
  props: ActivityHistoryFilters,
  group: IActivityHistoryCustomFilterGroup,
) => {
  if (filter.type === ActivityHistoryFilterType.Dropdown) {
    const elements = filter?.options.map((option) => ({ value: option.id, label: option.title || option.id }));
    return when(
      elements.length > 0,
      () => html`
      <dss-input-dropdown
        inputsize="md"
        icon=""
        multiple
        dropdownFixed
        type="default"
        .elements=${elements}
        @onInputDropdownChange=${(e) => props._onChangeCustomFilterAllValues(group.id, filter.id, e.detail.selectedValue)}
        .selectedValue=${filter.enabledValues}
        selectorStyle="max-height: 450px"
      >
        <label slot="label">${filter.title}</label>
        <input slot="input" type="text" class="dss-input" autocomplete="off" ?disabled=${!group.enabled} />
      </dss-input-dropdown>
    `,
    );
  }

  return filter.singleOption
    ? getFilterTemplate(filter, props._onChangeCustomFilterValue.bind(props), group)
    : when(
        !!filter.options?.length,
        () => html`
        <primaria-accordion>
          <dss-typography slot="title" tag="p" variant="body-3" fontweight="regular">${filter.title}</dss-typography>
          <div class="custom-filter-content" slot="content">
            ${filter.options.map((option) =>
              getFilterTemplate(filter, props._onChangeCustomFilterValue.bind(props), group, option),
            )}
          </div>
        </primaria-accordion>
      `,
      );
};
